import { useEffect } from "react";

/**
 * Generic in-app modal/dialog.
 *
 * Replaces browser-native alert()/confirm() usage across the app so that
 * confirmations and status messages match InsightPulse AI's visual design
 * and work well in dark mode.
 */
export default function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = "md", // "sm" | "md" | "lg"
  dismissible = true,
}) {
  // Close on Escape key.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape" && dismissible) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, dismissible, onClose]);

  if (!open) return null;

  const maxWidth =
    size === "sm" ? "max-w-sm" : size === "lg" ? "max-w-lg" : "max-w-md";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "modal-title" : undefined}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-[1px]"
        onClick={() => dismissible && onClose?.()}
      />

      {/* Panel */}
      <div
        className={`relative w-full ${maxWidth} bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl shadow-black/50 max-h-[85vh] flex flex-col`}
      >
        {title && (
          <div className="px-6 pt-6 pb-1 shrink-0">
            <h2 id="modal-title" className="text-lg font-bold text-white">
              {title}
            </h2>
          </div>
        )}

        <div className="px-6 py-4 overflow-y-auto text-sm text-gray-300 leading-relaxed">
          {children}
        </div>

        {footer && (
          <div className="px-6 pb-6 pt-2 flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
